import React, { useState, useEffect, useCallback } from 'react';
import ErrorBoundary from './ErrorBoundary';
import { Lightbulb, Sun, Moon, CheckCircle, Pencil } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import EnhancedThinkMateChat from './EnhancedThinkMateChat';
import AutoFocusInput from './ui/AutoFocusInput';
import Button from './ui/button';
import persistenceService from '../utils/persistenceService';
import { getTimePeriod, TimezonePreferences, DEFAULT_TIMEZONE_PREFERENCES } from '../utils/timezone';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const daysOfWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const todayIdx = new Date().getDay();

const Dashboard: React.FC = () => {
  const [showInsightsPopup, setShowInsightsPopup] = useState(false);
  const [timezonePreferences, setTimezonePreferences] = useState<TimezonePreferences>(DEFAULT_TIMEZONE_PREFERENCES);
  const location = useLocation();
  const navigate = useNavigate();

  // Daily check-in status state
  const [dailyCheckInStatus, setDailyCheckInStatus] = useState<{
    morning: boolean;
    evening: boolean;
    completionStatus: 'both' | 'morning-only' | 'evening-only' | 'none';
    streakDay: number;
    date: string;
  } | null>(null);

  // Real-time stats state
  const [stats, setStats] = useState([
    { label: 'Streak', value: '...' },
    { label: 'Log', value: '...' },
    { label: 'Words', value: '...' },
  ]);
  
  // Cache stats to avoid unnecessary recalculations
  const [statsCache, setStatsCache] = useState<{
    timestamp: number;
    data: any[];
  } | null>(null);

  // State for Ask Think Mate
  const [aiInput, setAiInput] = useState('');

  // Add state to control AskThinkMateChat modal
  const [showAskThinkMate, setShowAskThinkMate] = useState(false);
  const [initialAiQuestion, setInitialAiQuestion] = useState<string | null>(null);

  // Optimized streak calculation with performance improvements
  const calculateStreak = useCallback((entries: any[]) => {
    if (entries.length === 0) return 0;

    // Limit processing to recent entries for performance (365 days max)
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    const recentEntries = entries.filter(entry => new Date(entry.date) >= oneYearAgo);
    if (recentEntries.length === 0) return 0;

    // Create a Set of unique dates for O(1) lookup performance
    const uniqueDatesSet = new Set(
      recentEntries.map(entry => {
        const date = new Date(entry.date);
        // Use toDateString() for cleaner date comparison
        return date.toDateString();
      })
    );

    if (uniqueDatesSet.size === 0) return 0;

    let streak = 0;
    let currentDate = new Date();
    
    // Count consecutive days from today backwards (max 365 days)
    const maxDaysToCheck = Math.min(365, uniqueDatesSet.size + 30); // +30 buffer for gaps
    
    for (let i = 0; i < maxDaysToCheck; i++) {
      const checkDateStr = currentDate.toDateString();
      
      if (uniqueDatesSet.has(checkDateStr)) {
        streak++;
      } else if (streak > 0) {
        // Break on first gap after starting streak
        break;
      }
      
      // Move to previous day
      currentDate = new Date(currentDate);
      currentDate.setDate(currentDate.getDate() - 1);
    }

    return streak;
  }, []);

  // Optimized real-time statistics calculation with caching
  const calculateRealStats = useCallback(async (forceRefresh = false) => {
    // Check cache first (5-minute cache)
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();
    
    if (!forceRefresh && statsCache && (now - statsCache.timestamp) < CACHE_DURATION) {
      setStats(statsCache.data);
      return;
    }
    
    // If no auth token, show starter defaults instead of loading
    const token = localStorage.getItem('token');
    if (!token) {
      setStats([
        { label: 'Streak', value: '0' },
        { label: 'Log', value: '0' },
        { label: 'Words', value: '0' },
      ]);
      return;
    }
    
    try {
      // Get recent entries with reasonable limit for performance
      const entriesResponse = await persistenceService.getEntries({ 
        limit: 100 // Maximum allowed by server validation
        // Note: Backend sorts by date DESC by default, no need to specify
      });
      const entries = entriesResponse.entries;

      if (entries.length === 0) {
        setStats([
          { label: 'Streak', value: '0' },
          { label: 'Log', value: '0' },
          { label: 'Words', value: '0' },
        ]);
        return;
      }

      // Batch calculations for efficiency
      let totalWords = 0;
      const entriesCount = entries.length;
      
      // Single pass through entries for word count calculation
      for (const entry of entries) {
        totalWords += entry.metadata?.wordCount || 0;
      }

      // Calculate streak with optimized function
      const streak = calculateStreak(entries);

      // Use streak from dailyCheckInStatus if available, otherwise calculate from entries
      const streakValue = dailyCheckInStatus?.streakDay || streak;
      
      // Update stats with formatted values
      const newStats = [
        { 
          label: 'Streak', 
          value: streakValue >= 2 ? `🔥 ${streakValue}` : `${streakValue}` 
        },
        { 
          label: 'Log', 
          value: entriesCount.toString() 
        },
        { 
          label: 'Words', 
          value: totalWords.toLocaleString() // Format large numbers with commas
        },
      ];
      
      setStats(newStats);
      
      // Update cache
      setStatsCache({
        timestamp: now,
        data: newStats
      });
    } catch (error) {
      console.warn('Failed to calculate stats:', error);
      // Set fallback stats instead of keeping loading state
      setStats([
        { label: 'Streak', value: '0' },
        { label: 'Log', value: '0' },
        { label: 'Words', value: '0' },
      ]);
    }
  }, [calculateStreak, statsCache]);

  // Load check-in completion status and calculate real stats
  useEffect(() => {
    // Force scroll to top on component mount/refresh with slight delay
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 0);
    
    const loadDataAndStats = async () => {
      try {
        // Load user preferences including timezone
        const preferences = await persistenceService.getPreferences();
        if (preferences.timezone) {
          setTimezonePreferences(preferences.timezone);
        }

        // Load daily check-in status from new API (with small delay to avoid rate limits)
        await new Promise(resolve => setTimeout(resolve, 100));
        const token = localStorage.getItem('token');
        if (token) {
          const headers: Record<string, string> = {
            'x-auth-token': token
          };
          
          // Add access granted header if password protection was bypassed
          const accessGranted = localStorage.getItem('thinkmate_access_granted');
          if (accessGranted === 'true') {
            headers['x-access-granted'] = 'true';
          }
          
          const response = await fetch(`${API_URL}/api/daily-checkins/today`, {
            headers
          });
          
          if (response.ok) {
            const data = await response.json();
            setDailyCheckInStatus({
              morning: !!data.morning,
              evening: !!data.evening,
              completionStatus: data.completionStatus || 'none',
              streakDay: data.streakDay || 0,
              date: data.date
            });
          }
        } else {
          // Set default check-in status for unauthenticated users
          setDailyCheckInStatus({
            morning: false,
            evening: false,
            completionStatus: 'none',
            streakDay: 0,
            date: new Date().toISOString().split('T')[0]
          });
        }

        // Calculate real statistics
        // Add delay to avoid rate limiting issues
        setTimeout(async () => {
          await calculateRealStats();
        }, 200);
      } catch (error) {
        console.warn('Failed to load dashboard data:', error);
      }
    };
    loadDataAndStats();
  }, [calculateRealStats]);

  // Show popup first time after onboarding
  useEffect(() => {
    if (localStorage.getItem('onboardingComplete') === 'true' && !localStorage.getItem('insightsPopupShown')) {
      setShowInsightsPopup(true);
      localStorage.setItem('insightsPopupShown', 'true');
    }
  }, []);

  // Auto-refresh check-in status when returning to dashboard
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      // Refresh check-in status when returning to dashboard
      const loadCheckInStatus = async () => {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const headers: Record<string, string> = {
              'x-auth-token': token
            };
            
            // Add access granted header if password protection was bypassed
            const accessGranted = localStorage.getItem('thinkmate_access_granted');
            if (accessGranted === 'true') {
              headers['x-access-granted'] = 'true';
            }
            
            const response = await fetch(`${API_URL}/api/daily-checkins/today`, {
              headers
            });
            
            if (response.ok) {
              const data = await response.json();
              setDailyCheckInStatus({
                morning: !!data.morning,
                evening: !!data.evening,
                completionStatus: data.completionStatus || 'none',
                streakDay: data.streakDay || 0,
                date: data.date
              });
            }
          } else {
            // Set default check-in status for unauthenticated users
            setDailyCheckInStatus({
              morning: false,
              evening: false,
              completionStatus: 'none',
              streakDay: 0,
              date: new Date().toISOString().split('T')[0]
            });
          }
        } catch (error) {
          console.warn('Failed to refresh check-in status:', error);
        }
      };
      loadCheckInStatus();
    }
  }, [location.pathname]);

  // Scroll to top when dashboard loads (especially after onboarding)
  useEffect(() => {
    // Check if we need to force scroll to top (e.g., from onboarding completion)
    if (sessionStorage.getItem('scrollToTop') === 'true') {
      sessionStorage.removeItem('scrollToTop');
      // Use setTimeout to ensure scroll happens after browser's automatic scroll restoration
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  // Handlers for check-in completion
  // Navigate to check-in chat interface
  const handleCheckInNavigation = () => {
    navigate('/check-in-chat');
  };

  // Get smart button text based on current status and time
  const getCheckInButtonText = () => {
    if (!dailyCheckInStatus) return 'Check-in';
    
    const { morning, evening, completionStatus } = dailyCheckInStatus;
    
    // If both are done, don't show button (handled in render)
    if (completionStatus === 'both') return '';
    
    // Determine what's available based on time and completion status
    const isMorningTime = timePeriod === 'morning' || timePeriod === 'afternoon';
    const isEveningTime = timePeriod === 'evening' || timePeriod === 'night';
    
    // Morning time logic
    if (isMorningTime) {
      if (!morning) return 'Check-in';
      if (!evening) return 'Check-in'; // Generic, will route to evening when time comes
    }
    
    // Evening time logic
    if (isEveningTime) {
      if (!evening) return 'Check-in';
      if (!morning) return 'Check-in'; // Generic, will route to morning next day
    }
    
    return 'Check-in';
  };

  const handleAiSend = async () => {
    if (!aiInput.trim()) return;
    setInitialAiQuestion(aiInput);
    setShowAskThinkMate(true);
    setAiInput('');
  };

  // Determine which card to show based on timezone-aware time
  const currentTime = new Date();
  const timePeriod = getTimePeriod(currentTime, timezonePreferences);

  return (
    <ErrorBoundary>
      <div className="w-full max-w-2xl mx-auto px-4 py-6 md:px-2 md:py-4 flex flex-col gap-6">
      {/* Day Selector */}
      <div className="flex justify-between items-center mb-4 md:mb-2 relative">
        {daysOfWeek.map((d, i) => (
          <div
            key={`day-${i}`}
            className={`w-10 h-10 md:w-8 md:h-8 flex items-center justify-center rounded-full border-2 ${i === todayIdx ? 'border-rose-500 text-rose-600 font-bold' : 'border-gray-200 text-text-secondary'} bg-white relative text-sm md:text-xs`}
          >
            {d}
            {i === todayIdx && <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-rose-100 rounded-full border border-rose-300" />}
          </div>
        ))}
        <button className="ml-3 md:ml-2 text-text-secondary hover:text-rose-500 p-2 md:p-0 min-h-[44px] md:min-h-auto flex items-center justify-center"><svg width="24" height="24" className="md:w-5 md:h-5" fill="none" viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10m-13 8V7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></button>
        {/* Insights Popup */}
        {showInsightsPopup && (
          <div className="absolute left-1/2 -translate-x-1/2 top-16 md:top-12 z-20">
            <div className="relative bg-yellow-50 border border-yellow-200 text-text-primary rounded-xl px-6 py-5 md:py-4 shadow flex items-center gap-3 text-base max-w-sm md:max-w-md min-w-[280px] md:min-w-[320px] mx-4 md:mx-0">
              <Lightbulb className="text-yellow-400 flex-shrink-0" size={22} />
              <span className="font-medium">Unlock personal insights every week<br className='block md:hidden'/> after writing 3 entries.</span>
              <button
                className="absolute top-2 right-2 text-text-secondary hover:text-text-primary text-lg focus:outline-none"
                onClick={() => setShowInsightsPopup(false)}
                aria-label="Close"
              >
                &times;
              </button>
              <span className="absolute left-1/2 -top-2 w-4 h-4 bg-yellow-50 border-l border-t border-yellow-200 rotate-45 -translate-x-1/2"></span>
            </div>
          </div>
        )}
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-3 gap-4 md:gap-3 mb-2">
        {stats.map(stat => (
          <div key={stat.label} className="bg-white rounded-xl shadow p-5 md:p-4 flex flex-col items-center justify-center min-h-[90px] md:min-h-[70px]">
            <div className="text-xs text-text-secondary font-medium mb-2 md:mb-1">{stat.label}</div>
            <div className="text-xl md:text-lg font-bold text-text-primary">{stat.value}</div>
          </div>
        ))}
      </div>
      {/* Daily Journaling - Show only morning or evening card based on time */}
      {/* Twice-Daily Check-in Card */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="bg-gray-100 text-text-secondary text-xs font-semibold rounded px-2 py-1">
              {(timePeriod === 'morning' || timePeriod === 'afternoon') ? 'Morning Intention' : 'Evening Reflection'}
            </span>
            <div className="flex items-center gap-1">
              {(timePeriod === 'morning' || timePeriod === 'afternoon') ? (
                <Sun className="text-yellow-400" size={16} />
              ) : (
                <Moon className="text-blue-400" size={16} />
              )}
            </div>
          </div>
          {dailyCheckInStatus?.completionStatus === 'both' && (
            <div className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <CheckCircle size={16} />
              <span>Complete</span>
            </div>
          )}
        </div>


        {/* Action Button */}
        {dailyCheckInStatus?.completionStatus === 'both' ? (
          <div className="text-center py-3">
            <div className="text-green-600 font-semibold mb-1">🎉 Both check-ins complete!</div>
            <div className="text-xs text-text-secondary">Great job staying consistent today</div>
          </div>
        ) : (
          <Button
            onClick={handleCheckInNavigation}
            className="w-full flex items-center justify-center gap-2"
            ambient={true}
            size="lg"
          >
            <Pencil size={20} />
            {getCheckInButtonText()}
          </Button>
        )}

        {/* Next Steps Hint */}
        {dailyCheckInStatus && dailyCheckInStatus.completionStatus !== 'both' && (
          <div className="text-xs text-text-secondary text-center mt-2">
            {timePeriod === 'morning' && !dailyCheckInStatus.morning && ''}
            {timePeriod === 'afternoon' && !dailyCheckInStatus.morning && 'Set your daily intention'}
            {timePeriod === 'evening' && !dailyCheckInStatus.evening && 'Time to reflect on your day'}
            {timePeriod === 'night' && !dailyCheckInStatus.evening && 'Complete your evening reflection'}
          </div>
        )}
      </div>
      {/* Growth Recipe + Ask AI - Responsive 2-column layout, equal height */}
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
        {/* Growth Recipe (left) */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 flex flex-col items-center justify-center text-center h-full min-h-[420px]">
          <div className="text-xs font-semibold text-text-secondary mb-4 tracking-wide uppercase">GROWTH RECIPE</div>
          <div className="text-base text-text-secondary mb-6 max-w-xs">Track the goals and practices that will lead to sustained life satisfaction.</div>
          <div className="flex gap-4">
            <Button
              variant="secondary"
              ambient={true}
              className="flex items-center gap-2 px-5 py-2 text-base shadow-sm"
            >
              Add Practice
            </Button>
            <Button
              variant="secondary"
              ambient={true}
              className="flex items-center gap-2 px-5 py-2 text-base shadow-sm"
            >
              Add goal
            </Button>
          </div>
        </div>
        {/* Ask AI (right) */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 flex flex-col h-full min-h-[420px]">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-semibold text-text-secondary tracking-wide uppercase">ASK THINK MATE</div>
            <div className="text-xs font-semibold text-text-secondary">All-time <span className="align-middle">▼</span></div>
          </div>
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden bg-white w-full focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-200 focus-within:shadow-lg transition-all duration-300">
              <span className="pl-3 pr-2 text-text-secondary flex items-center">
                <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><circle cx="10" cy="10" r="10" fill="#F3F4F6"/><text x="50%" y="55%" textAnchor="middle" fill="#A1A1AA" fontSize="13" fontFamily="Arial" dy=".3em">?</text></svg>
              </span>
              <AutoFocusInput
                type="text"
                placeholder="Write..."
                className="flex-1 py-4 md:py-3 bg-white text-text-primary font-inter border-none focus:ring-0 focus:outline-none h-14 md:h-12 text-base md:text-sm"
                value={aiInput}
                onChange={e => setAiInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAiSend(); }}
                disabled={false}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {[
              'What drains my energy most?',
              'What am I most grateful for?',
              'What are my coping mechanisms?',
              'What are my potential blind spots?',
              'What are some recurring themes from my entries?'
            ].map((q, i) => (
              <button
                key={`question-${i}`}
                className="text-left px-5 py-3 md:px-4 md:py-2 rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 text-text-primary font-medium text-base md:text-sm transition min-h-[48px] md:min-h-auto flex items-center"
                onClick={() => { 
                  setInitialAiQuestion(q);
                  setShowAskThinkMate(true);
                }}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>
      {showAskThinkMate && (
        <EnhancedThinkMateChat
          initialQuestion={initialAiQuestion || undefined}
          onClose={() => setShowAskThinkMate(false)}
        />
      )}
      </div>
    </ErrorBoundary>
  );
};

export default Dashboard;